// Sending attachments (issue #2): every image, video, file and voice note shows
// up in the chat immediately as a pending bubble with a local preview and
// upload progress, and turns into the real message when the server confirms
// it. A failed upload stays on screen with Retry instead of vanishing.
import { getDefaultStore } from 'jotai';
import { api, ApiError, type UploadedAttachment } from './api';
import {
  pendingUploadsAtom,
  returnedCaptionAtom,
  type PendingUpload,
} from '../store';
import type { SentMessage } from '../hooks/useConversationMessages';
import type { FileSecret } from './e2ee/crypto';
import { isE2EEReady, sendSealedAttachment, uploadSealed } from './e2ee/send';
import { sendSealed } from './e2ee/session';
import { albumEnvelope, type UploadedAlbumItem } from './albums';
import { ensurePlainUploadAllowed, KEYS_NOT_READY } from './e2ee/plainUpload';
import { isRetryable, queueMessage } from './outbox';
import { t } from '../i18n';
import { countClientFailure } from './errors';

export type AttachmentKind = 'image' | 'gif' | 'video' | 'voice' | 'file';

export interface AttachmentRequest {
  conversationId: string;
  kind: AttachmentKind;
  file: Blob;
  fileName: string;
  /** Sent with the upload; the server validates and echoes them back. */
  meta?: { width?: number; height?: number; duration?: number };
  thumbnail?: Blob;
  /** Shown in the pending bubble (an object URL; revoked when done). */
  previewUrl?: string | null;
  /** Quoted reply (M20, D20). */
  replyToId?: string;
  /** Extra message metadata, e.g. a voice note's waveform. */
  extraMetadata?: { waveform?: number[] };
  /**
   * Text to go after it (a lone album item's caption, D219): sent once it's
   * confirmed, and back in the message box if it's cancelled.
   */
  caption?: string;
  onSent: (message: SentMessage) => void;
}

/** One photo or video of an album, resized and with its preview. */
export interface AlbumItemInput {
  kind: 'image' | 'gif' | 'video';
  file: Blob;
  fileName: string;
  meta?: { width?: number; height?: number; duration?: number };
  thumbnail?: Blob;
  previewUrl: string | null;
}

/** An album on its way (M60, D219): each item uploads, then one message. */
export interface AlbumRequest {
  conversationId: string;
  /**
   * In order. An item still to be resized is a function that prepares it:
   * the bubble shows at once, each file goes up as soon as it's ready, and
   * one whose preparing failed is prepared again on Retry.
   */
  items: Array<
    AlbumItemInput | Promise<AlbumItemInput> | (() => Promise<AlbumItemInput>)
  >;
  /** What each item is, for the bubble's tiles before they're ready. */
  kinds?: AlbumItemInput['kind'][];
  /** Sealed with it (D219). */
  caption?: string;
  replyToId?: string;
  onSent: (message: SentMessage) => void;
}

const store = getDefaultStore();
const requests = new Map<string, AttachmentRequest>();
const albumRequests = new Map<string, AlbumRequest>();
// Album items once prepared, for their previews (revoked when done).
const albumPrepared = new Map<string, (AlbumItemInput | undefined)[]>();
// Album items being (or done being) prepared; a failed one is cleared so
// Retry prepares it again.
const albumStarted = new Map<string, (Promise<AlbumItemInput> | undefined)[]>();

/** An album item, preparing it if it isn't already (or failed before). */
function albumItem(id: string, index: number): Promise<AlbumItemInput> {
  const started = albumStarted.get(id) ?? [];
  albumStarted.set(id, started);
  const existing = started[index];
  if (existing) return existing;
  const source = albumRequests.get(id)?.items[index];
  const promise =
    typeof source === 'function'
      ? source()
      : source
        ? Promise.resolve(source)
        : Promise.reject(cancelled());
  started[index] = promise;
  promise.then(
    (ready) => showAlbumPreview(id, index, ready),
    () => {
      if (started[index] === promise) started[index] = undefined;
    }
  );
  return promise;
}

// A tile's preview, once its item is ready.
function showAlbumPreview(id: string, index: number, ready: AlbumItemInput) {
  const prepared = albumPrepared.get(id);
  if (!prepared) {
    if (ready.previewUrl) URL.revokeObjectURL(ready.previewUrl);
    return;
  }
  if (prepared[index]) return;
  prepared[index] = ready;
  store.set(pendingUploadsAtom, (list) =>
    list.map((entry) =>
      entry.id === id && entry.items
        ? {
            ...entry,
            fileSize: entry.fileSize + ready.file.size,
            items: entry.items.map((tile, i) =>
              i === index
                ? { ...tile, kind: ready.kind, previewUrl: ready.previewUrl }
                : tile
            ),
          }
        : entry
    )
  );
}
// Album items already up, by position: a retry sends only the rest (D134).
const albumUploaded = new Map<string, (UploadedAlbumItem | undefined)[]>();
const controllers = new Map<string, AbortController>();
// Uploads that finished: a retry only re-sends the message (D134).
const uploaded = new Map<
  string,
  {
    attachment: UploadedAttachment;
    secrets: { file: FileSecret; thumb?: FileSecret } | null;
  }
>();
const running = new Set<string>();
// Not uploaded: this device had no key yet for an encrypted chat (M28).
const waitingForKeys = new Set<string>();

// Waits between automatic tries after the connection drops (D134).
const RETRY_DELAYS_MS = [2000, 6000];
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const cancelled = () => new ApiError('Upload cancelled', 'UPLOAD_CANCELLED', 0);

function update(id: string, patch: Partial<PendingUpload>) {
  store.set(pendingUploadsAtom, (list) =>
    list.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
  );
}

function finish(id: string) {
  const request = requests.get(id);
  if (request?.previewUrl) URL.revokeObjectURL(request.previewUrl);
  albumPrepared.get(id)?.forEach((item) => {
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
  });
  albumPrepared.delete(id);
  albumStarted.delete(id);
  albumRequests.delete(id);
  albumUploaded.delete(id);
  requests.delete(id);
  controllers.delete(id);
  uploaded.delete(id);
  waitingForKeys.delete(id);
  store.set(pendingUploadsAtom, (list) =>
    list.filter((entry) => entry.id !== id)
  );
}

/**
 * Runs `work`, trying again by itself when it fails for a reason that can
 * clear up (no connection, a stalled upload, 408/429/5xx): twice, after 2
 * and 6 seconds (D134). The bubble says it's trying again meanwhile.
 */
export async function withRetries<T>(
  id: string,
  work: () => Promise<T>
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await work();
    } catch (err) {
      const isCancel =
        err instanceof ApiError && err.code === 'UPLOAD_CANCELLED';
      const delay = RETRY_DELAYS_MS[attempt];
      if (isCancel || delay === undefined || !isRetryable(err)) throw err;
      update(id, { retrying: true, progress: 0 });
      await sleep(delay);
      if (!requests.has(id) && !albumRequests.has(id)) throw cancelled();
    }
  }
}

function fail(
  id: string,
  err: unknown,
  fallback: string,
  counter: 'upload_failed' | 'send_failed' = 'upload_failed'
) {
  const autoRetry = isRetryable(err);
  // A failure that stays failed is counted, without any detail (M67, D254).
  if (!autoRetry) countClientFailure(counter);
  update(id, {
    status: 'failed',
    retrying: false,
    error: err instanceof ApiError ? err.message : fallback,
    // Back online or reconnected: tried again by itself (resumeAttachments).
    autoRetry,
  });
}

async function run(id: string) {
  if (running.has(id)) return;
  running.add(id);
  const once = albumRequests.has(id) ? runAlbumOnce : runOnce;
  try {
    if ((await once(id)) === 'expired') {
      // The server swept the file before the message reached it (D211):
      // upload it again, once, then send.
      await once(id, true);
    }
  } finally {
    running.delete(id);
  }
}

async function runOnce(
  id: string,
  expiredRetry = false
): Promise<'expired' | void> {
  const request = requests.get(id);
  if (!request) return;

  const controller = new AbortController();
  controllers.set(id, controller);

  // With this device's key, the file and its preview are encrypted before
  // they leave the device (M28, D59).
  let done = uploaded.get(id);
  if (!done) {
    update(id, {
      status: 'uploading',
      progress: 0,
      error: undefined,
      retrying: false,
      autoRetry: false,
    });
    const options = {
      scope: { conversationId: request.conversationId },
      signal: controller.signal,
      onProgress: (progress: number) =>
        update(id, { progress, retrying: false }),
    };
    try {
      done = await withRetries(id, async () => {
        if (isE2EEReady()) {
          const sealed = await uploadSealed(
            request.file,
            request.thumbnail,
            options
          );
          return {
            attachment: sealed.upload,
            secrets: { file: sealed.file, thumb: sealed.thumb },
          };
        }
        // No key here yet: never plaintext into an encrypted chat (M28).
        await ensurePlainUploadAllowed('chat', request.conversationId);
        const attachment = await api.uploadAttachment(
          request.file,
          request.fileName,
          request.meta,
          request.thumbnail,
          options
        );
        return { attachment, secrets: null };
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'UPLOAD_CANCELLED') return;
      if (err instanceof ApiError && err.code === KEYS_NOT_READY) {
        waitingForKeys.add(id);
      }
      fail(id, err, t('chat.send.uploadFailed'));
      return;
    }
    waitingForKeys.delete(id);
    uploaded.set(id, done);
  }

  const { attachment, secrets } = done;
  update(id, {
    status: 'sending',
    progress: 1,
    error: undefined,
    retrying: false,
    autoRetry: false,
  });
  try {
    const response = await withRetries(id, () =>
      secrets
        ? sendSealedAttachment({
            conversationId: request.conversationId,
            clientId: id,
            type: request.kind,
            replyToId: request.replyToId,
            upload: attachment,
            file: secrets.file,
            thumb: secrets.thumb,
            meta: {
              fileName: request.fileName,
              mimeType: request.file.type || 'application/octet-stream',
              fileSize: request.file.size,
              width: request.meta?.width,
              height: request.meta?.height,
              duration: request.meta?.duration,
              ...request.extraMetadata,
            },
          })
        : api.sendMessage({
            conversationId: request.conversationId,
            content: attachment.url,
            type: request.kind,
            // The pending id doubles as the clientId, so a retry can't post twice (D32).
            clientId: id,
            replyToId: request.replyToId,
            metadata: {
              fileName: attachment.fileName,
              fileSize: attachment.fileSize,
              mimeType: attachment.mimeType,
              width: attachment.width,
              height: attachment.height,
              duration: attachment.duration,
              thumbnailUrl: attachment.thumbnailUrl,
              ...request.extraMetadata,
            },
          })
    );
    request.onSent(response.data);
    // Its caption follows it, replying to the same message (D219).
    if (request.caption) {
      queueMessage({
        conversationId: request.conversationId,
        content: request.caption,
        replyToId: request.replyToId,
      });
    }
    finish(id);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'UPLOAD_CANCELLED') return;
    // The chat turned encrypted after a plaintext upload: Retry uploads
    // again, sealed, rather than offering the plaintext file again (D62).
    if (err instanceof ApiError && err.code === 'ENCRYPTION_REQUIRED') {
      uploaded.delete(id);
    }
    // The upload waited too long and was swept (410, D211): the cached one
    // is gone, so the file goes up again.
    if (err instanceof ApiError && err.code === 'UPLOAD_EXPIRED') {
      uploaded.delete(id);
      if (!expiredRetry) return 'expired';
    }
    fail(id, err, t('chat.send.couldNotSend'), 'send_failed');
  }
}

// One album tile's progress, and the album's as their average.
function updateItem(id: string, index: number, progress: number, done = false) {
  store.set(pendingUploadsAtom, (list) =>
    list.map((entry) => {
      if (entry.id !== id || !entry.items) return entry;
      const items = entry.items.map((item, i) =>
        i === index ? { ...item, progress, done: item.done || done } : item
      );
      return {
        ...entry,
        items,
        retrying: false,
        progress:
          items.reduce((sum, item) => sum + item.progress, 0) / items.length,
      };
    })
  );
}

/**
 * An album as one unit (D219): every file is sealed and uploaded with its
 * own retries, then one message lists them. Only what didn't make it is
 * uploaded again on Retry.
 */
async function runAlbumOnce(
  id: string,
  expiredRetry = false
): Promise<'expired' | void> {
  const request = albumRequests.get(id);
  if (!request) return;
  const controller = new AbortController();
  controllers.set(id, controller);
  const done = albumUploaded.get(id) ?? [];
  albumUploaded.set(id, done);

  update(id, {
    status: 'uploading',
    error: undefined,
    retrying: false,
    autoRetry: false,
  });
  for (const index of request.items.keys()) {
    if (done[index]) continue;
    try {
      const item = await albumItem(id, index);
      // Albums are always sealed (D219): wait for this chat's key.
      if (!isE2EEReady()) {
        throw new ApiError(t('e2ee.keysNotReady'), KEYS_NOT_READY, 409);
      }
      const sealed = await withRetries(id, () =>
        uploadSealed(item.file, item.thumbnail, {
          scope: { conversationId: request.conversationId },
          signal: controller.signal,
          onProgress: (progress) => updateItem(id, index, progress),
        })
      );
      done[index] = {
        kind: item.kind,
        upload: sealed.upload,
        file: sealed.file,
        thumb: sealed.thumb,
        mimeType: item.file.type || 'application/octet-stream',
        fileName: item.fileName,
        fileSize: item.file.size,
        width: item.meta?.width,
        height: item.meta?.height,
        duration: item.meta?.duration,
      };
      updateItem(id, index, 1, true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'UPLOAD_CANCELLED') return;
      if (err instanceof ApiError && err.code === KEYS_NOT_READY) {
        waitingForKeys.add(id);
      }
      fail(id, err, t('chat.send.uploadFailed'));
      return;
    }
  }
  waitingForKeys.delete(id);

  update(id, {
    status: 'sending',
    progress: 1,
    error: undefined,
    retrying: false,
    autoRetry: false,
  });
  try {
    const { envelope, files, counts } = albumEnvelope(
      done as UploadedAlbumItem[],
      request.caption
    );
    // The album's pending id is its clientId, so a retry can't post twice.
    const response = await withRetries(id, () =>
      sendSealed(
        request.conversationId,
        id,
        envelope,
        { type: 'album' },
        (sealed) =>
          api.sendMessage({
            conversationId: request.conversationId,
            clientId: id,
            replyToId: request.replyToId,
            type: 'album',
            content: sealed.content,
            keyEpoch: sealed.keyEpoch,
            encryption: sealed.encryption,
            metadata: { files, album: counts },
          })
      )
    );
    request.onSent(response.data);
    finish(id);
  } catch (err) {
    if (err instanceof ApiError && err.code === 'UPLOAD_CANCELLED') return;
    // Swept before the message reached it (D211): every file goes again.
    if (err instanceof ApiError && err.code === 'UPLOAD_EXPIRED') {
      albumUploaded.delete(id);
      if (!expiredRetry) return 'expired';
    }
    fail(id, err, t('chat.send.couldNotSend'), 'send_failed');
  }
}

/** Sends 2–10 photos or videos as one album (D219); returns its id. */
export function sendAlbum(request: AlbumRequest): string {
  const id = crypto.randomUUID();
  albumRequests.set(id, request);
  albumPrepared.set(id, []);
  store.set(pendingUploadsAtom, (list) => [
    ...list,
    {
      id,
      conversationId: request.conversationId,
      kind: 'album',
      fileName: '',
      fileSize: 0,
      previewUrl: null,
      items: request.items.map((item, index) => ({
        kind:
          (item instanceof Promise || typeof item === 'function'
            ? undefined
            : item.kind) ??
          request.kinds?.[index] ??
          'image',
        previewUrl: null,
        progress: 0,
        done: false,
      })),
      progress: 0,
      status: 'uploading',
    },
  ]);
  // Each tile shows its preview as soon as it's ready.
  request.items.forEach((_, index) => {
    void albumItem(id, index).catch(() => undefined);
  });
  void run(id);
  return id;
}

/**
 * Tries again every attachment that failed for a reason that can clear up,
 * when the browser comes back online or the socket reconnects (D134).
 */
export function resumeAttachments() {
  store
    .get(pendingUploadsAtom)
    .filter((upload) => upload.status === 'failed' && upload.autoRetry)
    .forEach((upload) => void run(upload.id));
}

/**
 * This chat's keys reached the device: attachments that waited for them
 * (KEYS_NOT_READY, nothing uploaded) go again.
 */
export function resumeAttachmentsForKeys(conversationId: string) {
  for (const id of [...waitingForKeys]) {
    const request = requests.get(id) ?? albumRequests.get(id);
    if (request?.conversationId === conversationId) void run(id);
  }
}

export function sendAttachment(request: AttachmentRequest): string {
  const id = crypto.randomUUID();
  requests.set(id, request);
  store.set(pendingUploadsAtom, (list) => [
    ...list,
    {
      id,
      conversationId: request.conversationId,
      kind: request.kind,
      fileName: request.fileName,
      fileSize: request.file.size,
      previewUrl: request.previewUrl ?? null,
      width: request.meta?.width,
      height: request.meta?.height,
      progress: 0,
      status: 'uploading',
    },
  ]);
  void run(id);
  return id;
}

export function retryAttachment(id: string) {
  void run(id);
}

export function cancelAttachment(id: string) {
  const request = requests.get(id);
  controllers.get(id)?.abort();
  finish(id);
  // A caption waiting for this photo isn't lost with it.
  if (request?.caption) {
    store.set(returnedCaptionAtom, {
      conversationId: request.conversationId,
      text: request.caption,
    });
  }
}

const THUMBNAIL_MAX = 480;

// Image dimensions plus a small JPEG preview (issue #2): the chat shows the
// thumbnail at once and only the lightbox fetches the full image. GIFs keep
// no thumbnail so they still animate in the bubble.
export async function prepareImage(file: File): Promise<{
  width?: number;
  height?: number;
  thumbnail?: Blob;
}> {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unreadable image'));
      element.src = url;
    });
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) return {};

    const scale = THUMBNAIL_MAX / Math.max(width, height);
    if (file.type === 'image/gif' || scale >= 1) return { width, height };

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    canvas
      .getContext('2d')
      ?.drawImage(image, 0, 0, canvas.width, canvas.height);
    const thumbnail = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.75)
    );
    return { width, height, thumbnail: thumbnail ?? undefined };
  } catch {
    // Not decodable here (e.g. HEIC in Chrome): send without a preview.
    return {};
  } finally {
    URL.revokeObjectURL(url);
  }
}
